// Rollen- und Rechte-Hooks (nur UI-Hinweise; Rechte prüft der Server). `useCan` nutzt die globalen
// Rechte aus /me (`permissions`), ressourcenbezogene Entscheidungen liefern die Antworten der API.
import { ADMIN_CAPABLE_ROLES, type Me, type RoleKey } from '@bar/contracts/core';
import { useMemo } from 'react';
import type { Viewer } from '@web/registry/types';
import { useAuth } from './context';

export function useRoles(): readonly RoleKey[] {
  return useAuth().me?.roles ?? [];
}

/** true, wenn das Konto mindestens eine der Rollen hat. */
export function useHasRole(...roles: RoleKey[]): boolean {
  const own = useRoles();
  return roles.some((role) => own.includes(role));
}

export function useIsAdminCapable(): boolean {
  return useHasRole(...ADMIN_CAPABLE_ROLES);
}

/** Globales Recht (z. B. 'cycles.manage', 'audit.read') laut Server. */
export function useCan(action: string): boolean {
  const me = useAuth().me;
  return Boolean(me?.permissions.includes(action));
}

/** Viewer für die Modul-Registry (Navigation, Sichtbarkeit) aus /me. */
export function viewerFromMe(me: Me | null): Viewer {
  if (!me) return { authenticated: false, roles: new Set() };
  return {
    authenticated: true,
    roles: new Set(me.roles),
    accountId: me.account.id,
    employeeId: me.employee?.id,
    displayName: me.employee?.displayName ?? me.account.displayName,
    ...(me.persona ? { persona: me.persona } : {}),
  };
}

export function useViewerFromAuth(): Viewer {
  const me = useAuth().me;
  return useMemo(() => viewerFromMe(me), [me]);
}

/** Heutiges Datum der Server-Uhr (APP_TODAY) für Fälligkeiten; ohne Anmeldung das lokale Datum. */
export function useToday(): string {
  const me = useAuth().me;
  return me?.today ?? new Date().toISOString().slice(0, 10);
}
